import { FormEvent, useState } from 'react'

import { ProjectInput, ProjectStatus } from 'api/types'
import { PROJECT_STATUS_LABELS } from 'lib/format'
import { t } from 'lib/i18n'

import { IconName } from './Icon'
import { MapPoint, ProjectLocationMap } from './ProjectLocationMap'
import { Button, SelectField, TextField } from './ui'

const EMPTY: ProjectInput = {
  client: '',
  consultant: '',
  contractor: '',
  endDate: '',
  location: '',
  name: '',
  startDate: '',
  status: 'ACTIVE',
  tenderNumber: '',
}

const STATUS_OPTIONS: ProjectStatus[] = ['ACTIVE', 'ON_HOLD', 'COMPLETED']

export const ProjectForm = ({
  initial,
  submitLabel,
  submitIcon = 'save',
  busy,
  onSubmit,
  onCancel,
  note,
}: {
  initial?: ProjectInput
  submitLabel: string
  submitIcon?: IconName
  busy?: boolean
  onSubmit: (input: ProjectInput) => void
  onCancel?: () => void
  note?: string
}) => {
  const [form, setForm] = useState<ProjectInput>(() => ({ ...EMPTY, ...initial }))
  const [latitude, setLatitude] = useState<number | null>(initial?.latitude ?? null)
  const [longitude, setLongitude] = useState<number | null>(initial?.longitude ?? null)
  const set = (key: keyof ProjectInput) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }))

  const rangeError = form.startDate && form.endDate && form.endDate < form.startDate ? t('تاريخ النهاية قبل تاريخ البداية', 'End date is before the start date') : undefined

  const setMapPoint = (point: MapPoint | null, address?: string) => {
    setLatitude(point?.latitude ?? null)
    setLongitude(point?.longitude ?? null)
    if (address) {
      setForm((current) => (current.location?.trim() ? current : { ...current, location: address }))
    }
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (rangeError) return
    onSubmit({
      client: form.client?.trim() || null,
      consultant: form.consultant?.trim() || null,
      contractor: form.contractor?.trim() || null,
      endDate: form.endDate,
      latitude,
      location: form.location?.trim() || null,
      longitude,
      name: form.name.trim(),
      startDate: form.startDate,
      status: form.status,
      tenderNumber: form.tenderNumber?.trim() || null,
    })
  }

  return (
    <form className="form-grid" onSubmit={submit}>
      <TextField label={t('اسم المشروع', 'Project name')} value={form.name} onChange={set('name')} required className="span-2" />
      <TextField label={t('رقم العطاء', 'Tender number')} value={form.tenderNumber ?? ''} onChange={set('tenderNumber')} />
      <TextField label={t('الموقع', 'Location')} value={form.location ?? ''} onChange={set('location')} />
      <ProjectLocationMap latitude={latitude} longitude={longitude} onChange={setMapPoint} />
      <TextField label={t('صاحب العمل', 'Client')} value={form.client ?? ''} onChange={set('client')} />
      <TextField label={t('المقاول', 'Contractor')} value={form.contractor ?? ''} onChange={set('contractor')} />
      <TextField label={t('الإشراف', 'Supervisor')} value={form.consultant ?? ''} onChange={set('consultant')} className="span-2" />
      <SelectField label={t('حالة المشروع', 'Project status')} value={form.status ?? 'ACTIVE'} onChange={set('status')}>
        {STATUS_OPTIONS.map((value) => (
          <option key={value} value={value}>
            {PROJECT_STATUS_LABELS[value]}
          </option>
        ))}
      </SelectField>
      <TextField label={t('تاريخ البداية', 'Start date')} type="date" value={form.startDate} onChange={set('startDate')} required />
      <TextField
        label={t('تاريخ النهاية', 'End date')}
        type="date"
        value={form.endDate}
        onChange={set('endDate')}
        required
        error={rangeError}
      />
      {note ? <p className="field__hint span-2">{note}</p> : null}
      <div className="form-actions span-2">
        {onCancel ? <Button onClick={onCancel}>{t('إلغاء', 'Cancel')}</Button> : null}
        <Button
          type="submit"
          variant="primary"
          icon={submitIcon}
          busy={busy}
          disabled={!form.name.trim() || !form.startDate || !form.endDate || Boolean(rangeError)}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
